import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Desktop, MapPin, Plus, Trash, ArrowUp, ArrowDown, Tag, CurrencyJpy } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth, isSimpleMenu } from '@/auth/AuthContext';
import { itApi } from '../it/itApi';

// IT資産マスタ — カテゴリ・設置場所の候補を管理（admin）。
export default function ITMasterSettings() {
  const [tab, setTab] = useState('categories');

  return (
    <div className="space-y-5 max-w-3xl" data-testid="it-master-page">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
          <Desktop size={24} weight="duotone" className="text-violet-600" /> IT資産マスタ
        </h1>
        <p className="text-sm text-zinc-500 mt-1">資産フォームで使うカテゴリ・設置場所の候補を管理します</p>
      </div>

      <div className="inline-flex items-center gap-1 rounded-lg bg-zinc-100 p-1" data-testid="it-master-tabs">
        {[
          { key: 'categories', label: 'カテゴリ', Icon: Tag },
          { key: 'locations', label: '設置場所', Icon: MapPin },
          { key: 'finance', label: '会計（耐用年数・少額）', Icon: CurrencyJpy },
        ].map(({ key, label, Icon }) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            data-testid={`it-master-tab-${key}`}
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md transition-colors ${
              tab === key ? 'bg-white text-violet-600 shadow-sm font-semibold' : 'text-zinc-500 hover:text-zinc-800'
            }`}>
            <Icon size={15} weight={tab === key ? 'bold' : 'regular'} /> {label}
          </button>
        ))}
      </div>

      {tab === 'categories' && <CategoriesTab />}
      {tab === 'locations' && <LocationsTab />}
      {tab === 'finance' && <FinanceTab />}
    </div>
  );
}

// A. 耐用年数のカテゴリ別既定と少額即時費用化のしきい値。
// 既定は **空** で出荷する（経理が決める。こちらで数字を推測しない）。
function FinanceTab() {
  const [cats, setCats] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [threshold, setThreshold] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([itApi.assetCategories(), itApi.financeSettings()])
      .then(([c, f]) => {
        setCats(c.categories || []);
        setDefaults(f.useful_life_defaults || {});
        setThreshold(f.minor_amount_threshold == null ? '' : String(f.minor_amount_threshold));
      })
      .catch(() => toast.error('会計設定の読み込みに失敗しました'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    const payload = {};
    Object.entries(defaults).forEach(([k, v]) => {
      if (v !== '' && v != null) payload[k] = Number(v);
    });
    setSaving(true);
    try {
      const r = await itApi.saveFinanceSettings({
        useful_life_defaults: payload,
        minor_amount_threshold: threshold === '' ? null : Number(threshold),
      });
      setDefaults(r.useful_life_defaults || {});
      setThreshold(r.minor_amount_threshold == null ? '' : String(r.minor_amount_threshold));
      toast.success('会計設定を保存しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-16 text-center text-sm text-zinc-400">読込中…</div>;

  const filled = Object.values(defaults).filter((v) => v !== '' && v != null).length;

  return (
    <div className="space-y-4" data-testid="it-finance-page">
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
        data-testid="it-finance-note">
        簿価は <b>定額法・購入日からの月割り・残存1円</b> で計算します。
        耐用年数と少額のしきい値は <b>経理が決める値</b> のため、既定は空のままです。
        空の間は簿価を償却せず、資産詳細に「耐用年数 未設定」と表示します（勝手に推測しません）。
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-semibold text-zinc-700">
          カテゴリ別の耐用年数（年）<span className="ml-2 text-xs font-normal text-zinc-400">設定済み {filled} / {cats.length}</span>
        </p>
        <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="it-finance-save">
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 divide-y divide-zinc-200 shadow-sm" data-testid="it-finance-list">
        {cats.map((c, i) => (
          <div key={c} className="flex items-center gap-3 px-3 py-2" data-testid={`it-finance-row-${i}`}>
            <span className="flex-1 text-sm text-zinc-700">{c}</span>
            <Input type="number" min="0" max="100" step="0.5"
              value={defaults[c] ?? ''}
              onChange={(e) => setDefaults((d) => ({ ...d, [c]: e.target.value }))}
              placeholder="未設定" className="h-9 w-28 text-right tabular-nums"
              data-testid={`it-finance-life-${i}`} />
            <span className="w-6 text-xs text-zinc-400">年</span>
          </div>
        ))}
        {cats.length === 0 && <div className="px-4 py-8 text-center text-sm text-zinc-400">カテゴリがありません</div>}
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 p-3 shadow-sm" data-testid="it-finance-threshold-card">
        <p className="text-sm font-semibold text-zinc-700">少額資産の即時費用化しきい値（円）</p>
        <p className="mt-0.5 text-[11px] text-zinc-400">
          この金額<b>未満</b>の資産は償却せず、簿価 0 として扱います。空欄 = 判定しない。
        </p>
        <Input type="number" min="0" value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          placeholder="未設定（例: 100000）" className="mt-2 h-10 w-48 tabular-nums"
          data-testid="it-finance-threshold" />
      </div>
    </div>
  );
}

function CategoriesTab() {
  const [cats, setCats] = useState([]);
  const [used, setUsed] = useState([]);
  const [newCat, setNewCat] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    itApi.assetCategories().then((r) => {
      setCats(r.managed?.length ? r.managed : r.categories || []);
      setUsed(r.categories || []);
    }).catch(() => toast.error('カテゴリの読み込みに失敗しました'))
      .finally(() => setLoading(false));
  }, []);

  const add = () => {
    const v = newCat.trim();
    if (!v) return;
    if (cats.includes(v)) { toast.error('同じカテゴリが既にあります'); return; }
    setCats((c) => [...c, v]);
    setNewCat('');
  };
  const remove = (i) => setCats((c) => c.filter((_, x) => x !== i));
  const move = (i, dir) => setCats((c) => {
    const n = [...c];
    const j = i + dir;
    if (j < 0 || j >= n.length) return c;
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });
  const rename = (i, v) => setCats((c) => c.map((x, xi) => (xi === i ? v : x)));

  const save = async () => {
    setSaving(true);
    try {
      const r = await itApi.saveAssetCategories(cats.map((c) => c.trim()).filter(Boolean));
      setCats(r.categories || []);
      toast.success('カテゴリを保存しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-16 text-center text-sm text-zinc-400">読込中…</div>;

  return (
    <div className="space-y-4" data-testid="it-categories-page">
      <div className="flex gap-2 items-center justify-between flex-wrap">
        <div className="flex gap-2">
          <Input value={newCat} onChange={(e) => setNewCat(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder="新しいカテゴリ名…" className="h-10 w-64" data-testid="it-categories-new-input" />
          <Button variant="outline" onClick={add} className="h-10" data-testid="it-categories-add-btn">
            <Plus size={14} className="mr-1" /> 追加
          </Button>
        </div>
        <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="it-categories-save">
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-zinc-200 divide-y divide-zinc-200 shadow-sm" data-testid="it-categories-list">
        {cats.map((c, i) => (
          <div key={`${c}-${i}`} className="flex items-center gap-2 px-3 py-2" data-testid={`it-category-row-${i}`}>
            <Input value={c} onChange={(e) => rename(i, e.target.value)} className="h-9 flex-1" data-testid={`it-category-input-${i}`} />
            <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`it-category-up-${i}`}><ArrowUp size={14} /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400" onClick={() => move(i, 1)} disabled={i === cats.length - 1} data-testid={`it-category-down-${i}`}><ArrowDown size={14} /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-rose-500" onClick={() => remove(i)} data-testid={`it-category-delete-${i}`}><Trash size={14} /></Button>
          </div>
        ))}
        {cats.length === 0 && <div className="px-4 py-8 text-center text-sm text-zinc-400">カテゴリがありません — 上の入力欄から追加してください</div>}
      </div>

      <p className="text-[11px] text-zinc-400">
        ※ 既存資産で使用中のカテゴリ（{used.length}件）は削除してもフォームの候補に自動表示されます。
      </p>
    </div>
  );
}

function LocationsTab() {
  const { user } = useAuth();
  const readOnly = isSimpleMenu(user);   // 場所は 設定 → マスター → 場所 が正
  const [locs, setLocs] = useState([]);
  const [usedCount, setUsedCount] = useState(0);
  const [unmanaged, setUnmanaged] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    itApi.assetLocations().then((r) => {
      setLocs((r.managed || []).map((l) => ({ name: l.name || '', shelf: l.shelf || '', memo: l.memo || '' })));
      setUsedCount((r.locations || []).length);
      setUnmanaged(r.unmanaged || []);
    }).catch(() => toast.error('設置場所の読み込みに失敗しました'))
      .finally(() => setLoading(false));
  }, []);

  const add = () => setLocs((c) => [...c, { name: '', shelf: '', memo: '' }]);
  const remove = (i) => setLocs((c) => c.filter((_, x) => x !== i));
  const move = (i, dir) => setLocs((c) => {
    const n = [...c];
    const j = i + dir;
    if (j < 0 || j >= n.length) return c;
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });
  const edit = (i, key, v) => setLocs((c) => c.map((x, xi) => (xi === i ? { ...x, [key]: v } : x)));

  const save = async () => {
    const cleaned = locs.map((l) => ({ ...l, name: l.name.trim() })).filter((l) => l.name);
    setSaving(true);
    try {
      const r = await itApi.saveAssetLocations(cleaned);
      setLocs((r.locations || []).map((l) => ({ name: l.name || '', shelf: l.shelf || '', memo: l.memo || '' })));
      setUnmanaged((prev) => prev.filter((n) => !(r.locations || []).some((l) => l.name === n)));
      toast.success('設置場所を保存しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="py-16 text-center text-sm text-zinc-400">読込中…</div>;

  return (
    <div className="space-y-4" data-testid="it-locations-page">
      {readOnly && (
        <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2" data-testid="it-locations-readonly-note">
          <p className="text-xs font-semibold text-sky-900">場所は 設定 → マスター → 場所 で管理します</p>
          <p className="mt-0.5 text-[11px] text-sky-800">
            ここは今の値を確認するだけの画面です（全社でひとつの場所マスタを使います）。
            <Link to="/settings/locations" className="ml-1 font-semibold underline" data-testid="it-locations-master-link">
              場所マスタを開く
            </Link>
          </p>
        </div>
      )}
      {!readOnly && (
        <div className="flex gap-2 items-center justify-between flex-wrap">
          <Button variant="outline" onClick={add} className="h-10" data-testid="it-locations-add-btn">
            <Plus size={14} className="mr-1" /> 設置場所を追加
          </Button>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="it-locations-save">
            {saving ? '保存中…' : '保存'}
          </Button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="it-locations-list">
        <div className="hidden sm:grid grid-cols-[1fr_1fr_1.4fr_100px] gap-2 px-3 py-2 bg-zinc-50 text-[11px] font-semibold text-zinc-500 border-b border-zinc-200">
          <span>場所名 *</span><span>棚・詳細</span><span>メモ</span><span />
        </div>
        <div className="divide-y divide-zinc-200">
          {locs.map((l, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1.4fr_100px] gap-2 px-3 py-2 items-center" data-testid={`it-location-row-${i}`}>
              <Input value={l.name} onChange={(e) => edit(i, 'name', e.target.value)} readOnly={readOnly} placeholder="例: 本社2F IT室" className="h-9" data-testid={`it-location-name-${i}`} />
              <Input value={l.shelf} onChange={(e) => edit(i, 'shelf', e.target.value)} readOnly={readOnly} placeholder="例: 棚A-3" className="h-9" data-testid={`it-location-shelf-${i}`} />
              <Input value={l.memo} onChange={(e) => edit(i, 'memo', e.target.value)} readOnly={readOnly} placeholder="メモ（任意）" className="h-9" data-testid={`it-location-memo-${i}`} />
              {!readOnly && (
              <div className="flex items-center justify-end gap-0.5">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`it-location-up-${i}`}><ArrowUp size={14} /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400" onClick={() => move(i, 1)} disabled={i === locs.length - 1} data-testid={`it-location-down-${i}`}><ArrowDown size={14} /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-zinc-400 hover:text-rose-500" onClick={() => remove(i)} data-testid={`it-location-delete-${i}`}><Trash size={14} /></Button>
              </div>
              )}
            </div>
          ))}
          {locs.length === 0 && <div className="px-4 py-8 text-center text-sm text-zinc-400">設置場所がありません — 「設置場所を追加」から登録してください</div>}
        </div>
      </div>

      <p className="text-[11px] text-zinc-400">
        ※ ここで登録した場所は資産フォームでワンクリック選択できます。既存資産で使用中の場所（{usedCount}件）も候補に自動表示されます。
      </p>

      {/* マスタ未登録だが資産で使用中の値 — 取り込みデータの揺れがここに出る。
          押すとこの表に行が増えるので、保存でマスタに正式登録される。 */}
      {unmanaged.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="it-locations-unmanaged">
          <p className="text-xs font-semibold text-amber-800">
            マスタ未登録の場所が {unmanaged.length}件 あります（資産では使用中）
          </p>
          <p className="text-[11px] text-amber-700 mt-0.5">
            クリックすると上の表に追加されます。「保存」でマスタに正式登録されます。
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {unmanaged.map((n) => (
              <button key={n} type="button"
                onClick={() => {
                  if (locs.some((l) => l.name === n)) { toast.info(`「${n}」は既に表にあります`); return; }
                  setLocs((c) => [...c, { name: n, shelf: '', memo: '' }]);
                }}
                className="rounded-full border border-amber-300 bg-white px-2.5 py-1 text-xs text-amber-900 hover:bg-amber-100"
                data-testid={`it-location-adopt-${n}`}>
                ＋ {n}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
